import { autoinject, observable } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { FaxService } from 'app/fm/services/fax-service';
import * as FM from 'app/fm/models/essette-api';
import { validate } from 'app/shared/elements/ess-rule';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { I18N } from 'aurelia-i18n';
import { Toaster } from 'app/shared/lib/toaster';

interface IDocumentSplit {
    pageRange: string;
    local: {
        queue?: FM.FaxQueue | null
        priority?: FM.FaxPriority | null
        checkout: boolean
    }
}

@autoinject()
export class SplitDocumentAction extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////

    @bindable
    private faxDoc!: FM.FaxDocument;

    @bindable
    private queues: FM.FaxQueue[] = [];
    @bindable
    private priorities: FM.FaxPriority[] = [];

    private documentSplits: IDocumentSplit[] = [];
    private documentCountOptions: string[] = ['1', '2'];

    @observable({ changeHandler: 'buildSplits' })
    private numDocuments: number = 2;

    protected validate = validate;

    @bindable
    public returnToQueue!: () => Promise<void>;

    @bindable
    public getNextDocument!: () => Promise<void>;

    ////****************** Element References ***************************************////

    private $splitDocumentAction: SplitDocumentAction = this;
    private $splitDocumentForm!: IForm;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly faxService: FaxService,
        private readonly i18n: I18N,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected attached(): void {
        this.buildSplits();
    }

    ////****************** Events ***************************************************////

    public async checkoutChanged(documentSplit: IDocumentSplit, checked: boolean): Promise<void> {
        if(checked && !documentSplit.local.queue) {
            documentSplit.local.queue = this.faxDoc.queue;
        }
    }

    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        return false;
    }

    public resetForm(): void {
        this.documentSplits = [];
        this.numDocuments = 2;
    }

    ////****************** Functions ************************************************////
    public async buildSplits(): Promise<void> {
        if(!this.faxDoc) return;
        const pageCount = this.faxDoc?.details?.pageCount || 1;
        this.documentCountOptions = [];
        for(let i = 0; i < pageCount; i++) {
            this.documentCountOptions[i] = (i + 1).toString();
        }

        if(this.numDocuments <= 0 || Number.isNaN(this.numDocuments)) {
            this.numDocuments = 1;
        }

        this.documentSplits = this.documentSplits.filter((_, idx) => (1 + idx) <= this.numDocuments);
        var missingDocSplits = this.numDocuments - this.documentSplits.length;
        if(missingDocSplits > 0) {
            for(let i = 0; i < missingDocSplits; i++) {
                this.documentSplits.push({
                    pageRange: '',
                    local: { checkout: true, queue: this.faxDoc.queue }
                });
            }
        }
    }

    public copyPriority(priority: FM.FaxPriority): void {
        this.documentSplits.forEach(split => split.local.priority = priority);
    }

    public copyQueue(queue: FM.FaxQueue): void {
        this.documentSplits.forEach(split => split.local.queue = queue);
    }

    public validatePages(pages: string): boolean {
        const isValidPageNumber = (x: number) => !Number.isNaN(x) && x > 0 && x <= (this.faxDoc.details?.pageCount || 0);
        const segments = pages.split(',');
        if(pages.match(/[^0-9 ,-]/g)) return false;

        for(let seg of segments) {
            let range = seg.split('-').map(s => +s);
            if(!isValidPageNumber(range[0])) return false;
            if(range.length > 1) {
                if(!isValidPageNumber(range[1]) || range[1] <= range[0]) {
                    return false;
                }
            }
        }
        return true;
    }

    public removeDocumentSplit(split: IDocumentSplit): void {
        this.documentSplits = this.documentSplits.filter(x => x !== split);
        this.numDocuments -= 1;
        this.buildSplits();
    }

    public async submitAndReturn(): Promise<void> {
        let submitSuccess = await this.submitSplits();
        if(submitSuccess) {
            this.resetForm();
            this.returnToQueue();
        }
    }

    public async submitAndGetNext(): Promise<void> {
        let submitSuccess = await this.submitSplits();
        if(submitSuccess) {
            this.getNextDocument();
        }
    }

    public async cancelAndReturn(): Promise<void> {
        this.returnToQueue();
    }

    ////****************** Service Calls ********************************************////

    public async submitSplits(): Promise<boolean> {
        if(this.faxDoc?.notes?.length! > 0) {
            const title = this.i18n.tr('fm:local.splitNoteConfirm', { number: this.faxDoc?.notes?.length, what: this.i18n.tr('fm:faxNote.$long', { count: this.faxDoc?.notes?.length }) });
            const message = this.i18n.tr('fm:local.splitDocumentConfirm');

            const result = await confirmation.showCustom(message, title, confirmation.options.YesNo);
            if(result.dialogResponse === confirmation.options.NO) {
                //canceling means we just stop
                return Promise.resolve(false);
            }
        }

        this.faxDoc.splitTo = this.documentSplits.map(dSplit => {
            const { queue, checkout, priority } = dSplit.local;
            const pageRange = dSplit.pageRange;
            const pages = pageRange
                .replace(/\s/g, '')
                .replace(/(\d+)-(\d+)/g, (_, start: string, end: string) => {
                    let numbers = [];
                    for(let i = +start; i <= +end; i++) {
                        numbers.push(i);
                    }
                    return numbers.join(',');
                })
                .split(',');

            return {
                pages,
                routeTo: { id: queue?.id },
                checkout,
                priority: { id: priority?.id }
            }
        });

        let isSuccessful = false;

        try {
            this.state.beginSave();

            const result = await this.faxService.splitFaxDocument(this.faxDoc);

            isSuccessful = result.isSuccessful;
        }
        catch(err) {
            throw err;
        }
        finally {
            if(isSuccessful) {
                const message = this.i18n.tr('ui:messages.events.splitAndRouted', { what: this.i18n.tr('fm:faxDocument.$') });
                this.toaster.info().message(message).show();
            } else {
                const message = this.i18n.tr('ui:messages.events.splitAndRoutedFailed', { what: this.i18n.tr('fm:faxDocument.$') });
                this.toaster.error().message(message).show();
            }
            this.state.endSave();
            return isSuccessful;
        }
    }

}
